import { useMenuLinks } from "Hooks/Layout/useMenuLinks";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";
import { canRoleFollow } from "Utils/Social/socialRules";
import type { RootStateType } from "State/Store";
import type { ApplicationLink } from "Types/types";
import { filterByAccessRightCode } from "Utils/menuUtils";
import { IS_MERCHANT_STORY_AUTHORING_ENABLED } from "Utils/Stories/storyAuthoring";
import { useSelector } from "react-redux";
import { useUsersNotVerifiedQuery } from "Api/Queries/User/useUsersNotVerifiedQuery";
import { useUsersAwaitingBankQuery } from "Api/Queries/User/useUsersAwaitingBankQuery";
import { useFollowRequestCountQuery } from "Api/Queries/Social/useFollowRequestsQuery";
import { useIsChatCapable } from "Hooks/Chat/useIsChatCapable";
import { useUnreadChatCount } from "Hooks/Chat/useUnreadChatCount";
import { useOrbitLOnboardingActionRequiredCountQuery } from "Api/Queries/Merchants/useOrbitLOnboardingActionRequiredCountQuery";

const EMPTY_MENU = {
  topMenu: [],
  bottomMenu: [],
};

type UseMenuReturn = {
  topMenu: Array<ApplicationLink>;
  bottomMenu: Array<ApplicationLink>;
};

export const useMenu = (userType: AccessRoleCode | null): UseMenuReturn => {
  const authUser = useSelector((e: RootStateType) => e.auth).user;
  const currentUserAccessRightCodes =
    authUser?.accessRights?.map(({ code }) => code) || [];

  const isChatCapable = useIsChatCapable();

  const isAdminOrCO =
    userType === AccessRoleCodes.Admin ||
    userType === AccessRoleCodes.CustomerOnboarding;

  const { data: notVerified } = useUsersNotVerifiedQuery({
    enabled: isAdminOrCO,
  });
  const { data: awaitingBank } = useUsersAwaitingBankQuery({
    enabled: isAdminOrCO,
  });
  const { data: orbitLActionRequired } =
    useOrbitLOnboardingActionRequiredCountQuery({
      enabled: isAdminOrCO,
    });

  const toBeVerifiedBadge =
    (notVerified?.items?.length ?? 0) > 0 ||
    (awaitingBank?.items?.length ?? 0) > 0;

  const unreadChatCount = useUnreadChatCount();
  const merchantsBadge = (orbitLActionRequired?.actionRequiredCount ?? 0) > 0;

  // Only personas that may follow have the People link (business personas get the Followers link
  // instead) — skip the badge fetch elsewhere.
  const hasPeopleSurface = canRoleFollow(userType);
  const { data: followRequestCount } =
    useFollowRequestCountQuery(hasPeopleSurface);
  const peopleBadge = (followRequestCount?.pendingIncomingCount ?? 0) > 0;

  const {
    contact,
    generalHelp,
    logout,
    overview,
    transactions,
    bankAccounts,
    settings,
    users,
    licenses,
    merchantKeypadPayment,
    employeeKeypadPayment,
    merchants,
    allMerchants,
    unassignedMerchants,
    hunters,
    hunterPortal,
    feeCalculator,
    amlUsers,
    toBeVerified,
    paypoints,
    merchantSettlements,
    employees,
    thanksToYou,
    valor,
    notificationsManagement,
    stardust,
    people,
    followers,
    peopleSearch,
    whereToPay,
    campaigns,
    coupons,
    ssoClients,
    terminals,
    raffle,
    prizeDraw,
    rolesPermissions,
    chat,
    batchPayments,
    stories,
  } = useMenuLinks();

  const chatWithBadge = { ...chat, badge: unreadChatCount };

  const USER_TYPE_TO_MENU: Record<AccessRoleCode, UseMenuReturn> = {
    [AccessRoleCodes.Admin]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          users,
          { ...toBeVerified, badge: toBeVerifiedBadge },
          { ...merchants, badge: merchantsBadge },
          unassignedMerchants,
          hunters,
          campaigns,
          coupons,
          terminals,
          raffle,
          ssoClients,
          batchPayments,
          notificationsManagement,
          feeCalculator,
          stories,
          rolesPermissions,
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.AdminLite]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          users,
          { ...toBeVerified, badge: toBeVerifiedBadge },
          { ...merchants, badge: merchantsBadge },
          unassignedMerchants,
          hunters,
          campaigns,
          coupons,
          terminals,
          raffle,
          batchPayments,
          notificationsManagement,
          feeCalculator,
          rolesPermissions,
          chatWithBadge,
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.Hunter]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          merchants,
          hunterPortal,
          valor,
          peopleSearch,
          thanksToYou,
          licenses,
          feeCalculator,
          chatWithBadge,
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [contact, logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.Merchant]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          merchantKeypadPayment,
          transactions,
          merchantSettlements,
          paypoints,
          employees,
          batchPayments,
          followers,
          thanksToYou,
          chatWithBadge,
          // Brand-only launch: merchants get the Stories page only once their authoring is switched on.
          ...(IS_MERCHANT_STORY_AUTHORING_ENABLED ? [stories] : []),
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [contact, generalHelp, logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.Employee]: {
      topMenu: filterByAccessRightCode(
        [overview, employeeKeypadPayment, transactions, settings],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [generalHelp, logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.Customer]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          transactions,
          whereToPay,
          stardust,
          prizeDraw,
          { ...people, badge: peopleBadge },
          thanksToYou,
          bankAccounts,
          chatWithBadge,
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [contact, generalHelp, logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.Aml]: {
      topMenu: [overview, amlUsers, merchants],
      bottomMenu: [contact, logout],
    },
    [AccessRoleCodes.CustomerOnboarding]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          users,
          { ...merchants, badge: merchantsBadge },
          unassignedMerchants,
          hunters,
          { ...toBeVerified, badge: toBeVerifiedBadge },
          campaigns,
          coupons,
          chatWithBadge,
          terminals,
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [contact, generalHelp, logout],
        currentUserAccessRightCodes,
      ),
    },
    [AccessRoleCodes.HunterPlus]: {
      topMenu: filterByAccessRightCode(
        [
          overview,
          merchants,
          allMerchants,
          unassignedMerchants,
          hunters,
          hunterPortal,
          valor,
          peopleSearch,
          thanksToYou,
          licenses,
          feeCalculator,
          chatWithBadge,
          settings,
        ],
        currentUserAccessRightCodes,
      ),
      bottomMenu: filterByAccessRightCode(
        [contact, logout],
        currentUserAccessRightCodes,
      ),
    },
    Disponent: {
      topMenu: [],
      bottomMenu: [],
    },
    Child: {
      topMenu: [],
      bottomMenu: [],
    },
  };

  const menu = userType ? USER_TYPE_TO_MENU[userType] : EMPTY_MENU;

  if (isChatCapable) {
    return menu;
  }

  return {
    topMenu: menu.topMenu.filter((item) => item.url !== chat.url),
    bottomMenu: menu.bottomMenu.filter((item) => item.url !== chat.url),
  };
};
